'use client'

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import Image from 'next/image'
import ReactMarkdown from 'react-markdown'
import { sourcesApi } from '@/lib/api/sources'
import { insightsApi, SourceInsightResponse } from '@/lib/api/insights'
import { skillsApi } from '@/lib/api/skills'
import { embeddingApi } from '@/lib/api/embedding'
import { graphragApi } from '@/lib/api/graphrag'
import { QUERY_KEYS } from '@/lib/api/query-client'
import { SourceDetailResponse, SourceListResponse, SourceStatusResponse } from '@/lib/types/api'
import { SkillListItem } from '@/lib/api/skills'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { InlineEdit } from '@/components/common/InlineEdit'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Link as LinkIcon,
  Upload,
  AlignLeft,
  ExternalLink,
  Download,
  Copy,
  CheckCircle,
  Youtube,
  MoreVertical,
  Trash2,
  Sparkles,
  Plus,
  Lightbulb,
  Database,
  AlertCircle,
  ImageIcon,
  MessageSquare,
  Network,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { toast } from 'sonner'
import { SourceInsightDialog } from '@/components/source/SourceInsightDialog'
import { NotebookAssociations } from '@/components/source/NotebookAssociations'
import { useI18n } from '@/lib/hooks/use-i18n'
import { getDateFnsLocale } from '@/lib/i18n/date-fns'
import { useModelDefaults } from '@/lib/hooks/use-models'
import { getLocalizedField } from '@/lib/i18n/translations'

interface SourceDetailContentProps {
  sourceId: string
  showChatButton?: boolean
  onChatClick?: () => void
  onClose?: () => void
}

interface ParsedSourceImage {
  url: string
  caption: string
  page?: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function imageBlockType(block: Record<string, unknown>): string {
  const rawType = block.type
  if (typeof rawType === 'string') return rawType
  if (isRecord(rawType)) {
    return stringValue(rawType.value || rawType.name)
  }
  return ''
}

function extractParsedImages(blocks: SourceDetailResponse['rag_content_list']): ParsedSourceImage[] {
  if (!Array.isArray(blocks)) return []

  return blocks
    .filter(isRecord)
    .map((block) => {
      if (imageBlockType(block).toLowerCase() !== 'image') return null

      const metadata = isRecord(block.metadata) ? block.metadata : {}
      const url = (
        stringValue(block.image_path) ||
        stringValue(block.img_path) ||
        stringValue(metadata.image_path) ||
        stringValue(metadata.img_path)
      )
      if (!url || !/^https?:\/\//i.test(url)) return null

      const pageValue = block.page_num ?? block.page ?? metadata.page_num ?? metadata.page
      const page = typeof pageValue === 'number' || typeof pageValue === 'string'
        ? String(pageValue)
        : undefined
      const caption = (
        stringValue(block.caption) ||
        stringValue(block.image_caption) ||
        stringValue(metadata.caption) ||
        stringValue(metadata.image_caption) ||
        stringValue(block.text)
      )

      return { url, caption, page }
    })
    .filter((item): item is ParsedSourceImage => Boolean(item))
}

export function SourceDetailContent({
  sourceId,
  showChatButton = false,
  onChatClick,
  onClose
}: SourceDetailContentProps) {
  const queryClient = useQueryClient()
  const { t, language } = useI18n()
  const [source, setSource] = useState<SourceDetailResponse | null>(null)
  const [insights, setInsights] = useState<SourceInsightResponse[]>([])
  const [skills, setSkills] = useState<SkillListItem[]>([])
  const [selectedSkill, setSelectedSkill] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [loadingInsights, setLoadingInsights] = useState(false)
  const [creatingInsight, setCreatingInsight] = useState(false)
  const [deletingInsightId, setDeletingInsightId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [isEmbedding, setIsEmbedding] = useState(false)
  const [isBuildingGraph, setIsBuildingGraph] = useState(false)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [fileAvailable, setFileAvailable] = useState<boolean | null>(null)
  const [selectedInsight, setSelectedInsight] = useState<SourceInsightResponse | null>(null)
  const [insightToDelete, setInsightToDelete] = useState<SourceInsightResponse | null>(null)
  const { data: modelDefaults } = useModelDefaults()
  const hasEmbeddingModel = !!modelDefaults?.default_embedding_model
  const graphragInfo = source?.graphrag_processing_info as Record<string, unknown> | undefined
  const graphragTotal = typeof graphragInfo?.total_chunks === 'number' ? graphragInfo.total_chunks : 0
  const graphragCompleted = typeof graphragInfo?.completed_chunks === 'number' ? graphragInfo.completed_chunks : 0
  const graphragFailed = typeof graphragInfo?.failed_chunks === 'number' ? graphragInfo.failed_chunks : 0
  const graphragInProgress = graphragInfo?.in_progress === true
  const graphragProgress = graphragTotal > 0
    ? Math.min(100, Math.max(0, (graphragCompleted / graphragTotal) * 100))
    : undefined
  const visibleGraphragProgress = graphragProgress ?? (graphragInProgress ? 8 : undefined)
  const isGraphBuilding = isBuildingGraph || graphragInProgress
  const parsedImages = useMemo(
    () => extractParsedImages(source?.rag_content_list),
    [source?.rag_content_list],
  )

  const normalizeSourceId = useCallback((id: string) => {
    return id.startsWith('source:') ? id.slice('source:'.length) : id
  }, [])

  const markGraphBuildStarted = useCallback(() => {
    const targetId = normalizeSourceId(source?.id || sourceId)

    const buildGraphInfo = (currentInfo: unknown): Record<string, unknown> => {
      const current = currentInfo && typeof currentInfo === 'object'
        ? currentInfo as Record<string, unknown>
        : {}
      return {
        ...current,
        total_chunks: typeof current.total_chunks === 'number' ? current.total_chunks : 0,
        completed_chunks: typeof current.completed_chunks === 'number' ? current.completed_chunks : 0,
        failed_chunks: typeof current.failed_chunks === 'number' ? current.failed_chunks : 0,
        in_progress: true,
        status: 'running',
      }
    }

    const updateSource = <T extends SourceListResponse>(item: T): T => {
      if (normalizeSourceId(item.id) !== targetId) {
        return item
      }

      return {
        ...item,
        graph_built: false,
        graphrag_processing_info: buildGraphInfo(item.graphrag_processing_info),
      }
    }

    const isSourceRecord = (value: unknown): value is SourceListResponse => {
      return !!value &&
        typeof value === 'object' &&
        typeof (value as SourceListResponse).id === 'string' &&
        ('asset' in value || 'graph_built' in value)
    }

    setSource((current) => current ? updateSource(current) : current)

    queryClient.setQueriesData<unknown>({ queryKey: ['sources'] }, (oldData: unknown) => {
      if (Array.isArray(oldData)) {
        return oldData.map((item) => isSourceRecord(item) ? updateSource(item) : item)
      }

      if (isSourceRecord(oldData)) {
        return updateSource(oldData)
      }

      return oldData
    })

    queryClient.setQueriesData<Record<string, SourceStatusResponse>>(
      { queryKey: ['sources', 'status-batch'] },
      (oldData: Record<string, SourceStatusResponse> | undefined) => {
        if (!oldData || typeof oldData !== 'object') {
          return oldData
        }

        const current = oldData[targetId]
        return {
          ...oldData,
          [targetId]: {
            ...current,
            status: current?.status,
            message: current?.message || t('sourceDetail.graphrag.building'),
            graphrag_processing_info: buildGraphInfo(current?.graphrag_processing_info),
          },
        }
      }
    )
  }, [normalizeSourceId, queryClient, source?.id, sourceId, t])

  const refreshSourceQueries = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ['sources'],
      refetchType: 'active'
    })
    await queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.source(sourceId),
      refetchType: 'active'
    })
  }, [queryClient, sourceId])

  const fetchSource = useCallback(async () => {
    try {
      setLoading(true)
      const data = await sourcesApi.get(sourceId)
      setSource(data)
      if (typeof data.file_available === 'boolean') {
        setFileAvailable(data.file_available)
      } else if (!data.asset?.file_path) {
        setFileAvailable(null)
      } else {
        setFileAvailable(null)
      }
    } catch (err) {
      console.error('Failed to fetch source:', err)
      setError(t('sourceDetail.error.load'))
    } finally {
      setLoading(false)
    }
  }, [sourceId, t])

  const fetchInsights = useCallback(async () => {
    try {
      setLoadingInsights(true)
      const data = await insightsApi.listForSource(sourceId)
      setInsights(data)
    } catch (err) {
      console.error('Failed to fetch insights:', err)
    } finally {
      setLoadingInsights(false)
    }
  }, [sourceId])

  const fetchSkills = useCallback(async () => {
    try {
      const data = await skillsApi.list()
      // Only show transformation-type skills
      setSkills(data.filter((s: SkillListItem) => s.skillType === 'transformation' && s.enabled))
    } catch (err) {
      console.error('Failed to fetch skills:', err)
    }
  }, [])

  useEffect(() => {
    if (sourceId) {
      void fetchSource()
      void fetchInsights()
      void fetchSkills()
    }
  }, [fetchInsights, fetchSource, fetchSkills, sourceId])

  const createInsight = async () => {
    if (!selectedSkill) {
      toast.error(t('sourceDetail.error.selectSkill'))
      return
    }

    try {
      setCreatingInsight(true)
      await insightsApi.create(sourceId, {
        transformation_id: selectedSkill,  // legacy field name, maps to skill_id in backend
        language: language === 'zh' ? 'zh' : 'en'
      })
      toast.success(t('sourceDetail.success.insightCreated'))
      await fetchInsights()
      setSelectedSkill('')
    } catch (err) {
      console.error('Failed to create insight:', err)
      toast.error(t('sourceDetail.error.createInsight'))
    } finally {
      setCreatingInsight(false)
    }
  }

  const handleDeleteInsight = async () => {
    if (!insightToDelete) return

    try {
      setDeletingInsightId(insightToDelete.id)
      await insightsApi.delete(insightToDelete.id)
      toast.success(t('sourceDetail.insights.deleted') || 'Insight deleted')
      if (selectedInsight?.id === insightToDelete.id) {
        setSelectedInsight(null)
      }
      await fetchInsights()
    } catch (err) {
      console.error('Failed to delete insight:', err)
      toast.error(t('sourceDetail.insights.deleteError') || 'Failed to delete insight')
    } finally {
      setDeletingInsightId(null)
      setInsightToDelete(null)
    }
  }

  const handleUpdateTitle = async (title: string) => {
    if (!source || title === source.title) return

    try {
      await sourcesApi.update(sourceId, { title })
      toast.success(t('sourceDetail.success.titleUpdated'))
      setSource({ ...source, title })
    } catch (err) {
      console.error('Failed to update source title:', err)
      toast.error(t('sourceDetail.error.updateTitle'))
      await fetchSource()
    }
  }

  const handleEmbedContent = async () => {
    if (!source) return
    if (!hasEmbeddingModel) {
      toast.error(t('search.ask.error.noEmbeddingModel'))
      return
    }

    try {
      setIsEmbedding(true)
      const response = await embeddingApi.embedContent(sourceId, 'source', true)
      toast.success(response.message)
      await refreshSourceQueries()
      await fetchSource()
    } catch (err) {
      console.error('Failed to embed content:', err)
      toast.error(t('sourceDetail.error.embed'))
    } finally {
      setIsEmbedding(false)
    }
  }

  const handleBuildGraph = async () => {
    if (!source) return
    if (!hasEmbeddingModel) {
      toast.error(t('search.ask.error.noEmbeddingModel'))
      return
    }

    try {
      setIsBuildingGraph(true)
      const response = await graphragApi.buildGraph(sourceId)
      toast.success(response.message)
      markGraphBuildStarted()
      await refreshSourceQueries()
      await fetchSource()
    } catch (err) {
      console.error('Failed to build graph:', err)
      toast.error(t('sourceDetail.error.buildGraph'))
    } finally {
      setIsBuildingGraph(false)
    }
  }

  const extractFilename = (pathOrUrl: string | undefined, fallback: string) => {
    if (!pathOrUrl) {
      return fallback
    }
    const segments = pathOrUrl.split(/[/\\]/)
    return segments.pop() || fallback
  }

  const parseContentDisposition = (header?: string | null) => {
    if (!header) {
      return null
    }
    const match = header.match(/filename\*?=([^;]+)/i)
    if (!match) {
      return null
    }
    const value = match[1].trim()
    if (value.toLowerCase().startsWith("utf-8''")) {
      return decodeURIComponent(value.slice(7))
    }
    return value.replace(/^["']|["']$/g, '')
  }

  const handleDownloadFile = async () => {
    if (!source?.asset?.file_path || isDownloadingFile || fileAvailable === false) {
      return
    }

    try {
      setIsDownloadingFile(true)
      const response = await sourcesApi.downloadFile(source.id)
      const filenameFromHeader = parseContentDisposition(
        response.headers?.['content-disposition'] as string | undefined
      )
      const fallbackName = extractFilename(source.asset.file_path, `source-${source.id}`)
      const filename = filenameFromHeader || fallbackName

      const blobUrl = window.URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
      setFileAvailable(true)
      toast.success(t('sourceDetail.success.downloadStarted'))
    } catch (err) {
      console.error('Failed to download file:', err)
      if (isAxiosError(err) && err.response?.status === 404) {
        setFileAvailable(false)
        toast.error(t('sourceDetail.error.fileUnavailable'))
      } else {
        toast.error(t('sourceDetail.error.download'))
      }
    } finally {
      setIsDownloadingFile(false)
    }
  }

  const getSourceIcon = () => {
    if (!source) return null
    if (source.asset?.url) return <LinkIcon className="h-5 w-5" />
    if (source.asset?.file_path) return <Upload className="h-5 w-5" />
    return <AlignLeft className="h-5 w-5" />
  }

  const getSourceType = () => {
    if (!source) return t('sourceDetail.type.unknown')
    if (source.asset?.url) return t('sourceDetail.type.link')
    if (source.asset?.file_path) return t('sourceDetail.type.file')
    return t('sourceDetail.type.text')
  }

  const handleCopyUrl = useCallback(() => {
    if (source?.asset?.url) {
      navigator.clipboard.writeText(source.asset.url)
      setCopied(true)
      toast.success(t('sourceDetail.success.urlCopied'))
      setTimeout(() => setCopied(false), 2000)
    }
  }, [source, t])

  const handleOpenExternal = useCallback(() => {
    if (source?.asset?.url) {
      window.open(source.asset.url, '_blank')
    }
  }, [source])

  const getYouTubeVideoId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/
    ]

    for (const pattern of patterns) {
      const match = url.match(pattern)
      if (match) return match[1]
    }
    return null
  }

  const isYouTubeUrl = useMemo(() => {
    if (!source?.asset?.url) return false
    return !!(getYouTubeVideoId(source.asset.url))
  }, [source?.asset?.url])

  const youTubeVideoId = useMemo(() => {
    if (!source?.asset?.url) return null
    return getYouTubeVideoId(source.asset.url)
  }, [source?.asset?.url])

  const handleDelete = async () => {
    if (!source) return

    if (confirm(t('sourceDetail.confirm.delete'))) {
      try {
        await sourcesApi.delete(source.id)
        toast.success(t('sourceDetail.success.delete'))
        onClose?.()
      } catch (error) {
        console.error('Failed to delete source:', error)
        toast.error(t('sourceDetail.error.delete'))
      }
    }
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <LoadingSpinner />
      </div>
    )
  }

  if (error || !source) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-8">
        <p className="text-red-500">{error || t('sourceDetail.notFound')}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="pb-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <InlineEdit
              value={source.title || ''}
              onSave={handleUpdateTitle}
              className="text-2xl font-bold"
              inputClassName="text-2xl font-bold"
              placeholder={t('sourceDetail.title.placeholder')}
              emptyText={t('sourceDetail.title.untitled')}
            />
            <p className="mt-1 text-sm text-muted-foreground">
              {t('sourceDetail.sourceId', { id: source.id })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {getSourceIcon()}
            <Badge variant="secondary" className="text-sm">
              {getSourceType()}
            </Badge>

            {/* Chat with source button - only in modal */}
            {showChatButton && onChatClick && (
              <Button variant="outline" size="sm" onClick={onChatClick}>
                <MessageSquare className="h-4 w-4 mr-2" />
                {t('sourceDetail.chat')}
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {source.asset?.file_path && (
                  <>
                    <DropdownMenuItem
                      onClick={handleDownloadFile}
                      disabled={isDownloadingFile || fileAvailable === false}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      {fileAvailable === false
                        ? t('sourceDetail.file.unavailable')
                        : isDownloadingFile
                          ? t('sourceDetail.file.preparing')
                          : t('sourceDetail.file.download')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem
                  onClick={handleEmbedContent}
                  disabled={isEmbedding || source.embedded}
                >
                  <Database className="mr-2 h-4 w-4" />
                  {isEmbedding ? t('sourceDetail.embed.embedding') : source.embedded ? t('sourceDetail.embed.alreadyEmbedded') : t('sourceDetail.embed.action')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={handleDelete}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('sourceDetail.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Tabs Content */}
      <div className="flex-1 overflow-y-auto">
        <Tabs defaultValue="content" className="w-full">
          <TabsList className="grid w-full grid-cols-4 sticky top-0 z-10">
            <TabsTrigger value="content">{t('sourceDetail.tabs.content')}</TabsTrigger>
            <TabsTrigger value="images">
              {t('sourceDetail.tabs.images')} {parsedImages.length > 0 && `(${parsedImages.length})`}
            </TabsTrigger>
            <TabsTrigger value="insights">
              {t('sourceDetail.tabs.insights')} {insights.length > 0 && `(${insights.length})`}
            </TabsTrigger>
            <TabsTrigger value="details">{t('sourceDetail.tabs.details')}</TabsTrigger>
          </TabsList>

          <TabsContent value="content" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {isYouTubeUrl && <Youtube className="h-5 w-5" />}
                  {t('sourceDetail.tabs.content')}
                </CardTitle>
                {source.asset?.url && !isYouTubeUrl && (
                  <CardDescription className="flex items-center gap-2">
                    <LinkIcon className="h-4 w-4" />
                    <a
                      href={source.asset.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline text-blue-600"
                    >
                      {source.asset.url}
                    </a>
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                {isYouTubeUrl && youTubeVideoId && (
                  <div className="mb-6">
                    <div className="aspect-video rounded-lg overflow-hidden bg-black">
                      <iframe
                        src={`https://www.youtube.com/embed/${youTubeVideoId}`}
                        title="YouTube video"
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                    {source.asset?.url && (
                      <div className="mt-2">
                        <a
                          href={source.asset.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-muted-foreground hover:underline inline-flex items-center gap-1"
                        >
                          <ExternalLink className="h-3 w-3" />
                          {t('sourceDetail.youtube.open')}
                        </a>
                      </div>
                    )}
                  </div>
                )}
                <div className="prose prose-sm prose-neutral dark:prose-invert max-w-none prose-headings:font-semibold prose-a:text-blue-600 prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-p:mb-4 prose-p:leading-7 prose-li:mb-2">
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => <p className="mb-4">{children}</p>,
                      h1: ({ children }) => <h1 className="text-2xl font-bold mt-6 mb-4">{children}</h1>,
                      h2: ({ children }) => <h2 className="text-xl font-bold mt-5 mb-3">{children}</h2>,
                      h3: ({ children }) => <h3 className="text-lg font-semibold mt-4 mb-2">{children}</h3>,
                      ul: ({ children }) => <ul className="mb-4 list-disc pl-6">{children}</ul>,
                      ol: ({ children }) => <ol className="mb-4 list-decimal pl-6">{children}</ol>,
                      li: ({ children }) => <li className="mb-1">{children}</li>,
                    }}
                  >
                    {source.full_text || t('sourceDetail.content.empty')}
                  </ReactMarkdown>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="images" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2">
                    <ImageIcon className="h-5 w-5" />
                    {t('sourceDetail.tabs.images')}
                  </span>
                  <Badge variant="secondary">{parsedImages.length}</Badge>
                </CardTitle>
                <CardDescription>
                  {t('sourceDetail.images.description')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {parsedImages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center rounded-md border border-dashed py-10 text-center text-muted-foreground">
                    <ImageIcon className="mb-3 h-10 w-10 opacity-60" />
                    <p className="text-sm">{t('sourceDetail.images.empty')}</p>
                  </div>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {parsedImages.map((image, index) => (
                      <div key={`${image.url}-${index}`} className="overflow-hidden rounded-md border bg-background">
                        <a
                          href={image.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block bg-muted/40"
                        >
                          <div className="relative aspect-[4/3] w-full">
                            <Image
                              src={image.url}
                              alt={image.caption || `${t('sourceDetail.tabs.images')} ${index + 1}`}
                              fill
                              unoptimized
                              sizes="(min-width: 1280px) 33vw, (min-width: 640px) 50vw, 100vw"
                              className="object-contain"
                            />
                          </div>
                        </a>
                        <div className="space-y-2 p-3">
                          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                            <span>
                              {image.page
                                ? `${t('sourceDetail.images.page')} ${image.page}`
                                : `${t('sourceDetail.images.item')} ${index + 1}`}
                            </span>
                            <a
                              href={image.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 hover:text-foreground"
                            >
                              <ExternalLink className="h-3 w-3" />
                              {t('sourceDetail.images.open')}
                            </a>
                          </div>
                          {image.caption && (
                            <p className="line-clamp-2 text-sm leading-5 text-foreground">
                              {image.caption}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="insights" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Lightbulb className="h-5 w-5" />
                    {t('sourceDetail.tabs.insights')}
                  </span>
                  <Badge variant="secondary">{insights.length}</Badge>
                </CardTitle>
                <CardDescription>
                  {t('sourceDetail.insights.description')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Create New Insight */}
                <div className="rounded-lg border bg-muted/30 p-4">
                  <h3 className="mb-3 text-sm font-semibold flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    {t('sourceDetail.insights.generate')}
                  </h3>
                  <div className="flex gap-2">
                    <Select
                      value={selectedSkill}
                      onValueChange={setSelectedSkill}
                      disabled={creatingInsight}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder={t('sourceDetail.insights.selectSkill')} />
                      </SelectTrigger>
                      <SelectContent>
                        {skills.map((skill) => (
                          <SelectItem key={skill.id} value={skill.id}>
                            {getLocalizedField(skill, 'name', language) || skill.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      onClick={createInsight}
                      disabled={!selectedSkill || creatingInsight}
                    >
                      {creatingInsight ? (
                        <>
                          <LoadingSpinner className="mr-2 h-3 w-3" />
                          {t('sourceDetail.insights.creating')}
                        </>
                      ) : (
                        <>
                          <Plus className="mr-2 h-4 w-4" />
                          {t('sourceDetail.insights.create')}
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Insights List */}
                {loadingInsights ? (
                  <div className="flex items-center justify-center py-8">
                    <LoadingSpinner />
                  </div>
                ) : insights.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Lightbulb className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">{t('sourceDetail.insights.empty.title')}</p>
                    <p className="text-xs mt-1">{t('sourceDetail.insights.empty.description')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {insights.map((insight) => (
                      <div key={insight.id} className="rounded-lg border bg-background p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs uppercase">
                              {insight.insight_type}
                            </Badge>
                          </div>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setInsightToDelete(insight)}
                            disabled={deletingInsightId === insight.id}
                          >
                            {deletingInsightId === insight.id ? (
                              <LoadingSpinner className="h-4 w-4" />
                            ) : (
                              <Trash2 className="h-4 w-4 text-destructive" />
                            )}
                          </Button>
                        </div>
                        <p className="mt-2 text-sm text-muted-foreground">
                          {insight.content.slice(0, 180)}{insight.content.length > 180 ? '…' : ''}
                        </p>
                        <div className="mt-3 flex justify-end">
                          <Button size="sm" variant="outline" onClick={() => setSelectedInsight(insight)}>
                            {t('sourceDetail.insights.view')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="details" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('sourceDetail.tabs.details')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Embedding Alert */}
                {!source.embedded && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>
                      {t('sourceDetail.alert.notEmbedded.title')}
                    </AlertTitle>
                    <AlertDescription>
                      {t('sourceDetail.alert.notEmbedded.description')}
                      <div className="mt-3">
                        <Button
                          onClick={handleEmbedContent}
                          disabled={isEmbedding}
                          size="sm"
                        >
                          <Database className="mr-2 h-4 w-4" />
                          {isEmbedding ? t('sourceDetail.embed.embedding') : t('sourceDetail.embed.action')}
                        </Button>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}

                {/* GraphRAG Alert */}
                {!source.graph_built && (
                  <Alert>
                    <Network className="h-4 w-4" />
                    <AlertTitle>
                      {t('sourceDetail.alert.graphNotBuilt.title')}
                    </AlertTitle>
                    <AlertDescription>
                      {t('sourceDetail.alert.graphNotBuilt.description')}
                      <div className="mt-3">
                        <Button
                          onClick={handleBuildGraph}
                          disabled={isGraphBuilding || source.graph_built}
                          size="sm"
                        >
                          <Network className="mr-2 h-4 w-4" />
                          {isGraphBuilding ? t('sourceDetail.graphrag.building') : t('sourceDetail.graphrag.action')}
                        </Button>
                      </div>
                      {graphragInProgress && visibleGraphragProgress !== undefined && (
                        <div className="mt-3 space-y-2">
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>{t('sourceDetail.graphrag.progress')}</span>
                            <span>
                              {graphragTotal > 0
                                ? `${graphragCompleted}/${graphragTotal}`
                                : t('sourceDetail.graphrag.building')}
                            </span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-1.5">
                            <div
                              className={`bg-emerald-600 h-1.5 rounded-full transition-all duration-300 ${graphragProgress === undefined ? 'animate-pulse' : ''}`}
                              style={{ width: `${visibleGraphragProgress}%` }}
                            />
                          </div>
                        </div>
                      )}
                      {graphragFailed > 0 && (
                        <div className="mt-2 flex items-center gap-1 text-xs text-destructive">
                          <AlertCircle className="h-3 w-3" />
                          {t('sourceDetail.graphrag.failed', { count: graphragFailed })}
                        </div>
                      )}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Source Information */}
                <div className="space-y-4">
                  {source.asset?.url && (
                    <div>
                      <h3 className="mb-2 text-sm font-semibold">{t('sourceDetail.url')}</h3>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 rounded bg-muted px-2 py-1 text-sm">
                          {source.asset.url}
                        </code>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCopyUrl}
                        >
                          {copied ? (
                            <CheckCircle className="h-4 w-4" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleOpenExternal}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {source.asset?.file_path && (
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold">{t('sourceDetail.uploadedFile')}</h3>
                      <div className="flex flex-wrap items-center gap-2">
                        <code className="rounded bg-muted px-2 py-1 text-sm">
                          {source.asset.file_path}
                        </code>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleDownloadFile}
                          disabled={isDownloadingFile || fileAvailable === false}
                        >
                          <Download className="mr-2 h-4 w-4" />
                          {fileAvailable === false
                            ? t('sourceDetail.file.unavailableButton')
                            : isDownloadingFile
                              ? t('sourceDetail.file.preparingButton')
                              : t('sourceDetail.file.downloadButton')}
                        </Button>
                      </div>
                      {fileAvailable === false ? (
                        <p className="text-xs text-muted-foreground">
                          {t('sourceDetail.file.unavailableDescription')}
                        </p>
                      ) : null}
                    </div>
                  )}

                  {source.topics && source.topics.length > 0 && (
                    <div>
                      <h3 className="mb-2 text-sm font-semibold">{t('sourceDetail.topics')}</h3>
                      <div className="flex flex-wrap gap-2">
                        {source.topics.map((topic, idx) => (
                          <Badge key={idx} variant="outline">
                            {topic}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Metadata */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold">{t('sourceDetail.metadata')}</h3>
                    <div className="flex items-center gap-2">
                      <Database className="h-3.5 w-3.5 text-muted-foreground" />
                      <Badge variant={source.embedded ? "default" : "secondary"} className="text-xs">
                        {source.embedded ? t('sourceDetail.metadata.embedded') : t('sourceDetail.metadata.notEmbedded')}
                      </Badge>
                      <Network className="h-3.5 w-3.5 text-muted-foreground" />
                      <Badge
                        variant={source.graph_built ? "default" : "secondary"}
                        className="text-xs"
                        title={source.graph_built ? t('sourceDetail.metadata.entityCount', { count: source.entity_count }) : undefined}
                      >
                        {source.graph_built ? t('sourceDetail.metadata.graphBuilt') : t('sourceDetail.metadata.graphNotBuilt')}
                      </Badge>
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">{t('sourceDetail.metadata.created')}</p>
                      <p className="text-sm">
                        {formatDistanceToNow(new Date(source.created), {
                          addSuffix: true,
                          locale: getDateFnsLocale(language),
                        })}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(source.created).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">{t('sourceDetail.metadata.updated')}</p>
                      <p className="text-sm">
                        {formatDistanceToNow(new Date(source.updated), {
                          addSuffix: true,
                          locale: getDateFnsLocale(language),
                        })}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(source.updated).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notebook Associations */}
            <div className="mt-4">
              <NotebookAssociations
                sourceId={sourceId}
                currentNotebookIds={source.notebooks || []}
                onSave={fetchSource}
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <SourceInsightDialog
        open={Boolean(selectedInsight)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedInsight(null)
          }
        }}
        insight={selectedInsight ?? undefined}
      />
      <ConfirmDialog
        open={Boolean(insightToDelete)}
        onOpenChange={(open) => {
          if (!open) {
            setInsightToDelete(null)
          }
        }}
        title={t('sourceDetail.insights.confirmDeleteTitle')}
        description={t('sourceDetail.insights.confirmDeleteDescription')}
        confirmVariant="destructive"
        isLoading={Boolean(insightToDelete && deletingInsightId === insightToDelete.id)}
        onConfirm={handleDeleteInsight}
      />
    </div>
  )
}
